import { takeEvery, select, put, call, all } from "redux-saga/effects";
import {
  loading,
  saving,
  updateOptions,
} from "../../../store/Settings/actions";
import { loading as appLoading } from "../../../store/App/actions";
import { loadObjects, updateObject } from "../../../store/Objects/actions";
import {
  SAVE_SETTINGS,
  loadSettings,
} from "../../../store/Settings/actions/sets/Object";
import { cleanChanged } from "../../../store/Settings/actions/Changed";
import { getCleanedSettings } from "../../../utilities/getCleanedSettings";
import { getChangedSettings } from "../../../utilities/getChangedSettings";
import { buildGETUrl } from "../../../utilities/buildGETUrl";
import {
  showAlertError,
  showAlertInfo,
  showAlertSuccess,
} from "../../../store/Alert/actions";
import axios from "axios";
import { objectSort } from "../../Objects/loadObjects";
import { loadItems } from "../../../store/Settings/actions/sets/Object/Schedule";
import { updateObjectsIncludes } from "../../../store/ObjectsGroup/actions";
import { isMobile } from "../../../utilities/media";
import { tryToNumber } from "../../../components/Form/Select/Options";

const mobile = isMobile();

export default function* () {
  yield takeEvery(SAVE_SETTINGS, worker);
}

// Находит пересечение тегов всех объектов (общие теги)
const getCommonTags = (objects) => {
  if (!objects.length) return [];
  if (objects.length === 1) return normalizeTags(objects[0].tags);

  // Начинаем с тегов первого объекта
  let commonTags = new Set(normalizeTags(objects[0].tags));

  // Пересекаем с тегами каждого следующего объекта
  for (let i = 1; i < objects.length; i++) {
    const currentTags = new Set(normalizeTags(objects[i].tags));
    commonTags = new Set([...commonTags].filter((tag) => currentTags.has(tag)));
  }

  return [...commonTags];
};

// Нормализует теги: убирает пустые, конвертирует в числа
const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  const normalized = tags.filter(
    (tag) => tag !== "" && tag !== undefined && tag !== null,
  );
  if (normalized.length === 1 && normalized[0] === "") return [];
  return normalized.map(tryToNumber);
};

// Вычисляет diff тегов на основе общих тегов и новых настроек
const calculateTagsDiff = (commonTags, newSettingsTags) => {
  const normalizedNew = normalizeTags(newSettingsTags);

  const removed = commonTags
    .filter((tag) => !normalizedNew.includes(tag))
    .map((tag) => `-${tag}`);
  const added = normalizedNew
    .filter((tag) => !commonTags.includes(tag))
    .map((tag) => `+${tag}`);

  return {
    diffTags: [...removed, ...added],
    preservedTags: normalizedNew,
  };
};

// Применяет diff к исходным тегам объекта
const applyDiffToTags = (originalTags, diffTags) => {
  const normalized = normalizeTags(originalTags);
  
  diffTags.forEach((diff) => {
    if (diff.startsWith("+")) {
      const tag = diff.slice(1);
      if (!normalized.includes(tag)) {
        normalized.push(tag);
      }
    } else if (diff.startsWith("-")) {
      const tag = diff.slice(1);
      const index = normalized.indexOf(tag);
      if (index !== -1) {
        normalized.splice(index, 1);
      }
    }
  });
  
  return normalized;
};

function* worker({ payload }) {
  const { type, stateKey, fields } = payload;

  // Маркер для перезагрузки расписания
  let updateGroups = {};
  let needUpdateSchedule;

  if (mobile) {
    yield put(appLoading(true));
  }

  try {
    // Один select для всех данных
    const { selectedObjects, objects, changedMap, settings, statusList } =
      yield select((state) => ({
        selectedObjects: state.objects.selected || [],
        objects: state.objects.allObjects.filter((el) =>
          state.objects.selected?.includes(el.id),
        ),
        changedMap: state.settings.changed?.[type]?.[stateKey],
        settings: state.settings.settings?.[type]?.[stateKey] || null,
        statusList: state.objects.statusList,
      }));

    // Проверка на существование объектов
    if (!objects.length) {
      yield put(
        showAlertError({
          text: "Не выбраны объекты для сохранения.",
        }),
      );
      return;
    }

    // Обработка msgroup_id для первого объекта (если нужно для всех — расширить логику)
    if ("msgroup_id" in settings) {
      needUpdateSchedule = true;

      if (objects[0]?.msgroup_id) {
        updateGroups = {
          ...updateGroups,
          delete: { msgroup_id: objects[0].msgroup_id, id: objects[0].id },
        };
      }

      if (settings.msgroup_id) {
        updateGroups = {
          ...updateGroups,
          add: { msgroup_id: settings.msgroup_id, object: objects[0] },
        };
      }
    }

    if (!changedMap || !settings) {
      yield put(
        showAlertInfo({
          text: "Нет изменений для сохранения.",
        }),
      );
      return;
    }

    yield put(
      saving({
        type,
        stateKey,
        saving: true,
      }),
    );

    let settingsToSave = { ...settings };

    // Обрабатываем tags на основе пересечения всех выбранных объектов
    if (Array.isArray(settings["tags"])) {
      const commonTags = getCommonTags(objects);
      console.log({
        objectsCount: objects.length,
        commonTags,
        settingsTags: settings["tags"],
        objectsTags: objects.map((o) => o.tags),
      });

      const { diffTags } = calculateTagsDiff(commonTags, settings["tags"]);

      settingsToSave["tags"] = diffTags;

      console.log({ diffTags });
    }

    // Сохраняем
    let fetchedSave = yield call(
      fetchSave,
      selectedObjects,
      settingsToSave,
      changedMap,
    );

    // Ошибка
    if (fetchedSave.error) {
      yield put(
        showAlertError({
          errors: [`Ошибка сервера при ${SAVE_SETTINGS}.`, fetchedSave],
          text: "Не удалось сохранить изменения. Попробуйте повторить.",
        }),
      );
      return;
    }

    // Успех
    yield put(
      showAlertSuccess({
        responses: [`Успешное выполнение ${SAVE_SETTINGS}.`, fetchedSave],
        text: "Изменения успешно сохранены.",
      }),
    );

    const statusObject = statusList.reduce((acc, current) => {
      acc[current.id] = current.name;
      return acc;
    }, {});

    // Обновляем все затронутые объекты
    if (fetchedSave.success?.length) {
      for (const savedObject of fetchedSave.success) {
        const originalObject = objects.find((el) => el.id === savedObject.id);

        if (!originalObject) continue;

        const enrichedObject = {
          ...savedObject,
          hdd_size: originalObject.hdd_size,
          hdd_free: originalObject.hdd_free,
          streamlink: settings["streamlink"] || "",
        };

        let listObjectEntity = objectSort([enrichedObject], statusObject)[0];

        // Применяем персональные теги для каждого объекта
        if (Array.isArray(settings["tags"]) && objects.length > 0) {
          const commonTags = getCommonTags(objects);
          const { diffTags } = calculateTagsDiff(commonTags, settings["tags"]);
          
          const originalTags = normalizeTags(originalObject.tags);
          const personalizedTags = applyDiffToTags(originalTags, diffTags);
          
          listObjectEntity = {
            ...listObjectEntity,
            tags: personalizedTags,
          };
        }

        yield put(updateObject(listObjectEntity));
      }
    }

    yield put(
      cleanChanged({
        type,
        stateKey,
      }),
    );

    if (Object.keys(updateGroups).length) {
      yield put(updateObjectsIncludes(updateGroups));
    }
  } catch (e) {
    console.error("SAVE_SETTINGS error:", e);
    yield put(
      showAlertError({
        errors: [`Ошибка сети при ${SAVE_SETTINGS}.`, e],
        text: "Не удалось сохранить изменения. Попробуйте повторить.",
      }),
    );
  } finally {
    yield put(
      saving({
        type,
        stateKey,
        saving: false,
      }),
    );
    if (mobile) {
      yield put(appLoading(false));
    }
    if (needUpdateSchedule) {
      yield put(
        loadItems({
          stateKey,
          required: true,
        }),
      );
    }
  }
}

const setToNULL = ["pppoename", "pppoepass", "lip", "dns", "gateway"];

function fetchSave(ms_id, settings, changedMap) {
  settings = getCleanedSettings(settings);
  settings = getChangedSettings(changedMap, settings);

  let formData = new FormData();
  for (let name in settings) {
    if (setToNULL.includes(name) && !settings[name]) {
      formData.set(name, "NULL");
    } else {
      formData.set(name, settings[name]);
    }
  }

  return axios({
    url: "/msbox/update",
    method: "POST",
    data: formData,
    params: {
      id: ms_id,
    },
  }).then((response) => response.data);
}
